// =============================================================================
// SHANIZ GAMING ADMIN - USER DETAIL
// =============================================================================

import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  PowerSettingsNew as LogoutIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  userService,
  userDetailService,
  kycService,
  supportService,
  fraudService,
  KycSubmission,
  UserActivity,
  SupportTicket,
  RiskDevice,
} from '../services/api';
import { PermissionButton } from '../components/PermissionButton';

const TABS = ['Overview', 'KYC', 'Wallet', 'Activity', 'Devices', 'Risk', 'Tickets'] as const;

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  // Honour ?tab=wallet / ?tab=activity from the Users-list actions (was ignored,
  // so "View wallet"/"View activity" always opened Overview).
  const [searchParams] = useSearchParams();
  const initialTab = Math.max(
    0,
    TABS.findIndex((t) => t.toLowerCase() === (searchParams.get('tab') ?? '').toLowerCase()),
  );
  const [tab, setTab] = useState(initialTab);
  const [actionOpen, setActionOpen] = useState<'suspend' | 'unsuspend' | 'force_logout' | null>(null);
  const [reason, setReason] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const [activityPage, setActivityPage] = useState(0);
  const [activityRpp, setActivityRpp] = useState(20);
  const [fundOpen, setFundOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [fundReason, setFundReason] = useState('');

  const user = useQuery({ queryKey: ['user', id], queryFn: () => userService.getById(id!), enabled: Boolean(id) });
  const kyc = useQuery({
    queryKey: ['user-kyc', id],
    queryFn: () => kycService.getAll({ search: id }).then((r: any) => {
      // Backend uses `.kyc` key; other shapes use `.items` / `.data`.
      const list: KycSubmission[] = Array.isArray(r) ? r : (r?.items || r?.kyc || r?.data || []);
      return (Array.isArray(list) ? list : []).find((k) => k.userId === id) || null;
    }),
    enabled: Boolean(id) && tab === 1,
  });
  const coinHistory = useQuery({
    queryKey: ['user-coin-history', id],
    queryFn: () => userDetailService.getCoinHistory(id!),
    enabled: Boolean(id) && tab === 2,
  });
  const wallet = useQuery({
    queryKey: ['user-coins', id],
    queryFn: () => userDetailService.getCoins(id!),
    enabled: Boolean(id) && tab === 2,
  });
  const activity = useQuery({
    queryKey: ['user-activity', id, activityPage + 1, activityRpp],
    queryFn: () => userDetailService.getActivity(id!, activityPage + 1, activityRpp),
    enabled: Boolean(id) && tab === 3,
  });
  const devices = useQuery({
    queryKey: ['user-devices', id],
    queryFn: () => userDetailService.getDevices(id!),
    enabled: Boolean(id) && tab === 4,
  });
  const risk = useQuery({
    queryKey: ['user-risk', id],
    queryFn: () => fraudService.getUserRisk(id!),
    enabled: Boolean(id) && tab === 5,
  });
  const tickets = useQuery({
    queryKey: ['user-tickets', id],
    queryFn: () => supportService.getAll({ search: id, limit: 30 }),
    enabled: Boolean(id) && tab === 6,
  });

  const suspend = useMutation({
    mutationFn: (r: string) => userService.suspend(id!, r),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] });
      setActionOpen(null);
      setReason('');
      setSnackbar({ open: true, msg: 'User suspended', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Suspend failed', sev: 'error' }),
  });
  const unsuspend = useMutation({
    mutationFn: () => userService.unsuspend(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] });
      setActionOpen(null);
      setSnackbar({ open: true, msg: 'User unsuspended', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Unsuspend failed', sev: 'error' }),
  });
  const forceLogout = useMutation({
    mutationFn: () => userDetailService.forceLogout(id!),
    onSuccess: () => {
      setActionOpen(null);
      setSnackbar({ open: true, msg: 'Logged out all sessions', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Force logout failed', sev: 'error' }),
  });
  const fundWallet = useMutation({
    mutationFn: () => userDetailService.creditCoins(id!, Number(fundAmount), fundReason.trim() || 'Admin wallet funding'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-coins', id] });
      setFundOpen(false);
      setFundAmount('');
      setFundReason('');
      setSnackbar({ open: true, msg: `Funded ${fundAmount} coins`, sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Funding failed (needs SUPER_ADMIN)', sev: 'error' }),
  });

  if (!id) return <Alert severity="error">Missing user id.</Alert>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button startIcon={<BackIcon />} onClick={() => nav('/users')}>Back</Button>
      </Stack>

      {user.isLoading && <Skeleton variant="rectangular" height={160} />}
      {user.isError && <Alert severity="error">Failed to load user.</Alert>}
      {user.data && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar src={user.data.profilePicture || undefined} sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 32 }}>
                  {user.data.username?.charAt(0)?.toUpperCase()}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight={700}>{user.data.username}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.data.email || user.data.externalUserId}
                </Typography>
                <Stack direction="row" spacing={1} mt={1}>
                  <Chip size="small" label={user.data.role} color={user.data.role === 'SUPER_ADMIN' ? 'error' : 'default'} />
                  {user.data.isBanned ? (
                    <Chip size="small" color="error" icon={<BlockIcon />} label="Banned" />
                  ) : user.data.isActive ? (
                    <Chip size="small" color="success" icon={<ActiveIcon />} label="Active" />
                  ) : (
                    <Chip size="small" color="warning" label="Suspended" />
                  )}
                </Stack>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1}>
                  {user.data.isActive ? (
                    <PermissionButton permission="users.ban" color="warning" variant="outlined" onClick={() => setActionOpen('suspend')}>
                      Suspend
                    </PermissionButton>
                  ) : (
                    <PermissionButton permission="users.ban" color="success" variant="outlined" onClick={() => setActionOpen('unsuspend')}>
                      Unsuspend
                    </PermissionButton>
                  )}
                  <PermissionButton
                    permission="users.force_logout"
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={() => setActionOpen('force_logout')}
                  >
                    Force logout
                  </PermissionButton>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ borderBottom: '1px solid #262626' }}>
          {TABS.map((t) => (<Tab key={t} label={t} />))}
        </Tabs>
        <CardContent>
          {/* Overview */}
          {tab === 0 && user.data && (() => {
            const u: any = user.data;
            const games = (u._count?.sessionsAsPlayer1 ?? 0) + (u._count?.sessionsAsPlayer2 ?? 0);
            const wins = u._count?.sessionsAsWinner ?? 0;
            const losses = Math.max(0, games - wins);
            const antiCheat = Array.isArray(u.antiCheatEvents) ? u.antiCheatEvents.length : (u._count?.antiCheatEvents ?? 0);
            return (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Identity</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Username" secondary={u.username || '—'} /></ListItem>
                  <ListItem><ListItemText primary="Email" secondary={u.email || '—'} /></ListItem>
                  <ListItem><ListItemText primary="Phone" secondary={u.phoneNumber || '—'} /></ListItem>
                  <ListItem><ListItemText primary="Role" secondary={u.role || '—'} /></ListItem>
                  <ListItem><ListItemText primary="Tier" secondary={u.userTier || '—'} /></ListItem>
                  <ListItem><ListItemText primary="KYC status" secondary={u.kycStatus || '—'} /></ListItem>
                  <ListItem><ListItemText primary="Country" secondary={u.countryCode || '—'} /></ListItem>
                  <ListItem><ListItemText primary="User ID" secondary={u.id} /></ListItem>
                  <ListItem><ListItemText primary="Created" secondary={dayjs(u.createdAt).format('MMM D, YYYY HH:mm')} /></ListItem>
                  <ListItem><ListItemText primary="Last login" secondary={u.lastLoginAt ? dayjs(u.lastLoginAt).format('MMM D, YYYY HH:mm') : '—'} /></ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Play</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Total games" secondary={games} /></ListItem>
                  <ListItem><ListItemText primary="Wins" secondary={wins} /></ListItem>
                  <ListItem><ListItemText primary="Losses" secondary={losses} /></ListItem>
                  <ListItem><ListItemText primary="Anti-cheat events" secondary={antiCheat} /></ListItem>
                </List>
              </Grid>
              {u.isBanned && u.banReason && (
                <Grid item xs={12}>
                  <Alert severity="error">Ban reason: {u.banReason}</Alert>
                </Grid>
              )}
            </Grid>
            );
          })()}

          {/* KYC */}
          {tab === 1 && (
            <>
              {kyc.isLoading && <Skeleton variant="rectangular" height={200} />}
              {!kyc.isLoading && !kyc.data && <Alert severity="info">No KYC submission on file.</Alert>}
              {kyc.data && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box component="img" src={kyc.data.frontImageUrl} sx={{ width: '100%', borderRadius: 2 }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box component="img" src={kyc.data.backImageUrl} sx={{ width: '100%', borderRadius: 2 }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Backend uses `selfieUrl`, legacy `selfieImageUrl`. */}
                    <Box component="img" src={kyc.data.selfieImageUrl || (kyc.data as any).selfieUrl} sx={{ width: '100%', borderRadius: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Status: <Chip size="small" label={kyc.data.status} /></Typography>
                    <Typography variant="caption" color="text.secondary">Submitted {dayjs(kyc.data.submittedAt).format('MMM D HH:mm')}</Typography>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* Wallet */}
          {tab === 2 && (
            <>
              {wallet.isLoading && <Skeleton variant="rectangular" height={200} />}
              {wallet.data && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <PermissionButton
                        permission="users.edit"
                        variant="contained"
                        onClick={() => setFundOpen(true)}
                      >
                        Fund wallet
                      </PermissionButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#141414', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Balance (coins)</Typography>
                      <Typography variant="h5" fontWeight={700}>{wallet.data.balance?.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#141414', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Available</Typography>
                      <Typography variant="h6" fontWeight={700}>{wallet.data.available?.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#141414', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Locked (in play)</Typography>
                      <Typography variant="h6" fontWeight={700}>{wallet.data.locked?.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Recent coin activity</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(coinHistory.data || []).map((t, i) => {
                            const r = t as Record<string, any>;
                            return (
                              <TableRow key={r.id ?? i}>
                                <TableCell>{r.createdAt ? dayjs(r.createdAt).format('MMM D HH:mm') : '—'}</TableCell>
                                <TableCell>{r.type ?? r.entryType ?? '—'}</TableCell>
                                <TableCell>{Number(r.amount ?? 0).toLocaleString()}</TableCell>
                                <TableCell>{r.reason ?? r.description ?? '—'}</TableCell>
                              </TableRow>
                            );
                          })}
                          {(coinHistory.data || []).length === 0 && (
                            <TableRow><TableCell colSpan={4}><Typography color="text.secondary" textAlign="center" py={2}>No coin activity</Typography></TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* Activity */}
          {tab === 3 && (
            <>
              {activity.isLoading && <Skeleton variant="rectangular" height={240} />}
              {activity.data && (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>When</TableCell>
                          <TableCell>Kind</TableCell>
                          <TableCell>Summary</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const d: any = activity.data;
                          const rows: UserActivity[] = Array.isArray(d) ? d : (d?.items || d?.data || d?.events || d?.activity || []);
                          return (Array.isArray(rows) ? rows : []).map((a) => (
                            <TableRow key={a.id}>
                              <TableCell>{a.at ? dayjs(a.at).format('MMM D HH:mm:ss') : '—'}</TableCell>
                              <TableCell><Chip size="small" label={a.kind || '—'} /></TableCell>
                              <TableCell>{a.summary || ''}</TableCell>
                            </TableRow>
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={activity.data.pagination?.total ?? activity.data.total ?? 0}
                    page={activityPage}
                    rowsPerPage={activityRpp}
                    onPageChange={(_, p) => setActivityPage(p)}
                    onRowsPerPageChange={(e) => { setActivityRpp(parseInt(e.target.value, 10)); setActivityPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />
                </>
              )}
            </>
          )}

          {/* Devices */}
          {tab === 4 && (
            <>
              {devices.isLoading && <Skeleton variant="rectangular" height={200} />}
              {devices.data && (
                <List dense>
                  {(devices.data || []).map((d: RiskDevice) => (
                    <ListItem key={d.id}
                      secondaryAction={
                        <Stack direction="row" spacing={1}>
                          {d.trusted && <Chip size="small" color="success" label="Trusted" />}
                          {d.flagged && <Chip size="small" color="error" label="Flagged" />}
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={`${d.platform} · ${d.model || d.deviceId}`}
                        secondary={`IP ${d.ipAddress || ''} · ${d.geolocation?.city || ''} ${d.geolocation?.country || ''} · last seen ${dayjs(d.lastSeenAt).format('MMM D HH:mm')}`}
                      />
                    </ListItem>
                  ))}
                  {(devices.data || []).length === 0 && <Alert severity="info">No devices yet.</Alert>}
                </List>
              )}
            </>
          )}

          {/* Risk */}
          {tab === 5 && (
            <>
              {risk.isLoading && <Skeleton variant="rectangular" height={200} />}
              {risk.data && (() => {
                const rd: any = risk.data;
                const score = rd.score ?? rd.user ?? {};
                const scoreValue = score.score ?? score.riskScore ?? 0;
                const level = score.level ?? score.riskLevel ?? 'LOW';
                const events: any[] = Array.isArray(rd.events) ? rd.events : [];
                const devices: any[] = Array.isArray(rd.devices) ? rd.devices : [];
                return (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Risk score</Typography>
                      <Typography variant="h4" fontWeight={700}>{scoreValue}</Typography>
                      <Chip size="small" label={level} color={level === 'CRITICAL' || level === 'HIGH' ? 'error' : level === 'MEDIUM' ? 'warning' : 'default'} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle2" gutterBottom>Devices seen</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {devices.slice(0, 8).map((d) => (
                        <Chip key={d.id} size="small" label={`${(d.userAgent || '').slice(0, 20)}${(d.userAgent || '').length > 20 ? '…' : ''} · ${d.ip || ''}`} />
                      ))}
                      {devices.length === 0 && <Typography variant="body2" color="text.secondary">No devices</Typography>}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Risk events</Typography>
                    <List dense>
                      {events.map((e: any, i: number) => (
                        <ListItem key={e.id ?? i}>
                          <ListItemText primary={e.title || e.kind || 'Risk event'} secondary={`${e.kind || ''} · ${e.createdAt ? dayjs(e.createdAt).format('MMM D HH:mm') : ''}`} />
                        </ListItem>
                      ))}
                      {events.length === 0 && <Typography variant="body2" color="text.secondary">No risk events</Typography>}
                    </List>
                  </Grid>
                </Grid>
                );
              })()}
            </>
          )}

          {/* Tickets */}
          {tab === 6 && (
            <>
              {tickets.isLoading && <Skeleton variant="rectangular" height={200} />}
              {tickets.data && (
                <List dense>
                  {(((tickets.data.items || tickets.data.data || []) as SupportTicket[]) || []).map((t) => (
                    <ListItem key={t.id} secondaryAction={<Button size="small" onClick={() => nav(`/support/${t.id}`)}>Open</Button>}>
                      <ListItemText
                        primary={t.subject}
                        secondary={`${t.category} · ${t.status} · ${dayjs(t.updatedAt).format('MMM D')}`}
                      />
                    </ListItem>
                  ))}
                  {(((tickets.data.items || tickets.data.data || []) as SupportTicket[]) || []).length === 0 && (
                    <Typography color="text.secondary" textAlign="center" py={2}>No tickets.</Typography>
                  )}
                </List>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(actionOpen)} onClose={() => setActionOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionOpen === 'suspend' && 'Suspend user'}
          {actionOpen === 'unsuspend' && 'Unsuspend user'}
          {actionOpen === 'force_logout' && 'Force logout'}
        </DialogTitle>
        <DialogContent>
          {actionOpen === 'suspend' && (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
          {actionOpen === 'unsuspend' && <Typography>This will re-enable the account.</Typography>}
          {actionOpen === 'force_logout' && <Typography>This will invalidate all active sessions for this user.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionOpen(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={actionOpen === 'suspend' ? 'warning' : actionOpen === 'unsuspend' ? 'success' : 'primary'}
            disabled={(actionOpen === 'suspend' && !reason.trim()) || suspend.isPending || unsuspend.isPending || forceLogout.isPending}
            onClick={() => {
              if (actionOpen === 'suspend') suspend.mutate(reason.trim());
              else if (actionOpen === 'unsuspend') unsuspend.mutate();
              else if (actionOpen === 'force_logout') forceLogout.mutate();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fundOpen} onClose={() => setFundOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Fund wallet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Credit coins to this player&apos;s wallet from the platform treasury. Requires SUPER_ADMIN.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Amount (coins)"
            value={fundAmount}
            onChange={(e) => setFundAmount(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason"
            value={fundReason}
            onChange={(e) => setFundReason(e.target.value)}
            placeholder="e.g. Demo top-up"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFundOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!fundAmount || Number(fundAmount) <= 0 || fundWallet.isPending}
            onClick={() => fundWallet.mutate()}
          >
            {fundWallet.isPending ? 'Funding…' : 'Fund'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetail;
